import { UserPresenceType } from "@/types/presence"
import {
  ClientToServerEvents,
  ServerToClientEvents,
  SLOW_RESPONSE_VERSION,
  SOCKET_PATH,
  SubscribableObjectId,
  SubscribableObjectType,
} from "@/types/socket"
import { io, ManagerOptions, Socket, SocketOptions } from "socket.io-client"

import {
  dispatchAgentRunEvent,
  dispatchAgentRunReconnect,
} from "@/lib/agent-run-events"
import { AmplitudeEventAndProperties } from "@/lib/analytics/amplitude-types"
import { sendGTMEvent } from "@/lib/analytics/gtm-utils"
import { APP_INSTANCE_ID } from "@/lib/app-instance"
import { AppVersionEvents } from "@/lib/app-version-events"
import { dispatchAssistantSuggestionsEvent } from "@/lib/assistant-suggestions-events"
import { buildClientAxios } from "@/lib/client-axios"
import { getEmbeddedAuthToken } from "@/lib/embedded/token-storage"
import { dispatchForYouEvent } from "@/lib/for-you-events"
import { dispatchHotelSiteViewEvent } from "@/lib/hotel-site-view-events"
import { isEmbedded } from "@/lib/is-embedded"
import { dispatchProactiveAgentEvent } from "@/lib/proactive-agent-events"
import {
  dispatchCalendarSyncEvent,
  dispatchCalendarSyncReconnect,
} from "@/lib/storage/calendar-sync-runtime"
import { StreamedResponseFetcher } from "@/lib/streamed-response-fetcher"
import { assert } from "@/lib/utils/assertions"
import {
  traceVoiceChatObjectReceived,
  voiceTracer,
} from "@/lib/voice-trace/voice-trace"
import { NotificationManager } from "./notification-manager"
import { ObjectSubscriptionManager } from "./object-subscription-manager"
import { AbortSignalLike, ResponseBuffer } from "./response-buffer"

const socketTrace = voiceTracer("socket")

export class SocketManager {
  static _instance: SocketManager | undefined

  socket: Socket<ServerToClientEvents, ClientToServerEvents>
  responseBuffer: ResponseBuffer = new ResponseBuffer()
  streamedResponseFetcher: StreamedResponseFetcher

  private disconnectedDueToAuth = false
  private authFailures = 0
  private hasConnectedOnce = false
  private torndown = false
  private readonly MAX_AUTH_FAILURES = 3

  // The manager is app-lived: started on first demand and kept across route
  // navigations, so the socket, response waits, and server subscriptions
  // survive page-to-page moves. Sign-in/out are hard navigations, so the
  // socket never outlives an identity.
  static start() {
    this._instance ??= new SocketManager()
    return this._instance
  }

  // For boundaries that end the document's use of the socket entirely —
  // never route or layout cleanup.
  static disconnect() {
    if (this._instance) {
      this._instance.teardown()
      this._instance = undefined
    }
  }

  static async check() {
    if (this._instance) {
      await this._instance.checkSocket()
    }
  }

  static get instance() {
    assert(this._instance, "SocketManager not connected")
    return this._instance
  }

  static get hasInstance() {
    return !!this._instance
  }

  get connected() {
    return this.socket.connected
  }

  private constructor() {
    this.streamedResponseFetcher = new StreamedResponseFetcher(
      buildClientAxios(),
      this.responseBuffer,
      this.refreshTokenIfNeeded
    )
    this.socket = this.connect()
    this.streamedResponseFetcher.start()

    document.removeEventListener("visibilitychange", VisibilityChangeListener)
    document.addEventListener("visibilitychange", VisibilityChangeListener)
  }

  // Dynamic import: auth/client imports this module, so a static import
  // would be circular.
  private refreshTokenIfNeeded = async () => {
    const { refreshTokenIfNeeded } = await import("@/lib/auth/client")
    await refreshTokenIfNeeded()
  }

  // Wait for a streaming message of type "response", like those
  // generated by the server's SlowResponseStreamer. An aborted signal
  // withdraws the wait entirely, so the fetcher stops polling for it.
  async waitForResponse<T>(
    streamingResponseId: string,
    signal?: AbortSignalLike
  ): Promise<T> {
    const promise = this.responseBuffer.waitForResponse<T>(
      streamingResponseId,
      { signal }
    )
    this.streamedResponseFetcher.waiterRegistered(streamingResponseId)
    return promise
  }

  // Subscribe to updates for a particular object, and call the given listener
  // until unsubscribed. This is a low-level method; you probably want
  // ObjectSubscriptionManager.instance.subscribe() instead.
  async subscribe(
    objectType: SubscribableObjectType,
    objectId: SubscribableObjectId
  ): Promise<string> {
    const response = await this.socket.emitWithAck(
      "subscribe",
      objectType,
      objectId
    )

    // new-style response handling
    if (response.status === "authorized") {
      return response.subscriptionId
    } else if (response.status === "not authorized") {
      throw new Error("not authorized")
    } else {
      throw new Error(response.reason)
    }
  }

  unsubscribe(event: string) {
    this.socket.emit("unsubscribe", event)
  }

  announcePresenceChange(tripId: number, presenceType: UserPresenceType) {
    this.socket.emit("announce-presence-change", tripId, presenceType)
  }

  private connect() {
    console.debug("connecting socket", { appInstanceId: APP_INSTANCE_ID })

    const socketOptions: Partial<ManagerOptions & SocketOptions> = {
      path: SOCKET_PATH,

      // SocketIO supports a polling fallback for situations where real
      // websockets aren't allowed, probably rare in 2025. Nice, but that
      // feature requires the server to have session affinity enabled so
      // that multiple requests from a given client go to the same server,
      // and for other reasons we may want to disable that on heroku.
      // So disable polling; use websockets only.
      //
      // Also that the embed case doesn't work with polling because
      // it passes the auth token as a socket option.
      transports: ["websocket"],
    }

    // Declares which slow-response delivery scheme this client implements
    // and which app instance this socket belongs to; the socket server
    // tailors delivery per socket and joins the instance room accordingly.
    socketOptions.auth = {
      slowResponseVersion: SLOW_RESPONSE_VERSION,
      appInstanceId: APP_INSTANCE_ID,
    }

    if (isEmbedded()) {
      // iframe does not support cookies, so we pass auth token as a socket option and
      // use only websocket transport.
      const authToken = getEmbeddedAuthToken()
      socketOptions.auth = { ...socketOptions.auth, token: authToken }
    }

    const socket: Socket<ServerToClientEvents, ClientToServerEvents> =
      io(socketOptions)

    socket.on("connect", () => {
      console.debug("connected")
      socketTrace.info("socket_connected")
      this.authFailures = 0
      if (this.hasConnectedOnce) {
        dispatchCalendarSyncReconnect()
        dispatchAgentRunReconnect()
      } else {
        this.hasConnectedOnce = true
      }
      ObjectSubscriptionManager.instance.onSocketConnected()
    })
    socket.on("auth_expired", () => {
      console.debug("[SocketManager] Server reports auth expired")
      socketTrace.warn("socket_auth_expired")
      this.disconnectedDueToAuth = true
    })
    socket.on("disconnect", (reason) => {
      console.debug("disconnected", reason)
      socketTrace.warn("socket_disconnected", { reason })
      if (reason === "io server disconnect" && this.disconnectedDueToAuth) {
        this.disconnectedDueToAuth = false
        console.debug(
          "[SocketManager] Auth-related disconnect, refreshing token and reconnecting"
        )
        void this.handleAuthFailure()
      }
    })
    socket.on("connect_error", (err) => {
      console.debug("[SocketManager] connect_error", err.message)
      socketTrace.warn("socket_connect_error", {
        message: err.message,
        authFailures: this.authFailures,
      })
      if (err.message === "auth_expired") {
        this.authFailures++
        if (this.authFailures > this.MAX_AUTH_FAILURES) {
          console.error(
            "[SocketManager] Too many auth failures on connect, giving up"
          )
          return
        }
        // Stop auto-reconnect, force token refresh, then manually reconnect
        socket.disconnect()
        // Use dynamic import to avoid circular dependency
        void import("@/lib/auth/client").then(({ refreshAuthToken }) => {
          void refreshAuthToken().then(() => {
            // The manager may have been torn down (or this socket replaced,
            // in embedded mode) while the refresh was in flight; reconnecting
            // then would leave an orphan socket alive outside the manager.
            if (this.torndown || socket !== this.socket) return
            console.debug("[SocketManager] Token refreshed, reconnecting")
            socket.connect()
          })
        })
      }
    })
    // This client declares slowResponseVersion 2 and is fetch-only: the
    // server sends it the ready signal, never the body-carrying
    // "slow-response" event (that event still exists for version-1 clients).
    // A response the server couldn't stash fails at the wait deadline.
    socket.on("slow-response-ready", (id) =>
      this.streamedResponseFetcher.responseReady(id)
    )
    socket.on(
      "app-version",
      (version) => version && AppVersionEvents.instance.emit(version)
    )
    socket.on("notification", (data) => NotificationManager.instance.emit(data))
    socket.on("object", (payload) => {
      traceVoiceChatObjectReceived(payload)
      ObjectSubscriptionManager.instance.emit(payload)
    })
    socket.on("analytics", (data) => {
      sendGTMEvent(data as AmplitudeEventAndProperties)
    })
    socket.on("calendar_sync", (data) => {
      dispatchCalendarSyncEvent(data)
    })
    socket.on("assistant_suggestions", (data) => {
      dispatchAssistantSuggestionsEvent(data)
    })
    socket.on("for_you", (data) => {
      dispatchForYouEvent(data)
    })
    socket.on("proactive_agent", (data) => {
      dispatchProactiveAgentEvent(data)
    })
    socket.on("agent_run", (data) => {
      dispatchAgentRunEvent(data)
    })
    socket.on("hotel_site_view", (data) => {
      dispatchHotelSiteViewEvent(data)
    })

    return socket
  }

  private teardown() {
    console.debug("client disconnecting socket")
    this.torndown = true
    this.streamedResponseFetcher.stop()
    this.socket.disconnect()
    document.removeEventListener("visibilitychange", VisibilityChangeListener)
  }

  private async handleAuthFailure() {
    this.authFailures++
    if (this.authFailures > this.MAX_AUTH_FAILURES) {
      console.error("[SocketManager] Too many auth failures, giving up")
      return
    }

    try {
      // Force token refresh since we know auth is expired
      // Use dynamic import to avoid circular dependency
      const { refreshAuthToken } = await import("@/lib/auth/client")
      await refreshAuthToken()
      if (this.torndown) return
      this.socket.connect()
    } catch (e) {
      console.error("[SocketManager] Failed to recover from auth failure:", e)
    }
  }

  async checkSocket() {
    if (this.socket.connected) {
      return
    }
    if (this.socket.active) {
      // Socket is not connected but it's either trying to connect
      // or will soon try to reconnect.
      return
    }

    console.debug("attempt socket reconnect")

    try {
      await this.refreshTokenIfNeeded()
    } catch (error) {
      console.error(
        "Failed to refresh token before reconnecting socket:",
        error
      )
      // Continue with reconnection attempt anyway
    }

    // An explicit teardown while the refresh was in flight wins: reconnecting
    // here would resurrect a socket the manager no longer tracks.
    if (this.torndown) return

    // For embedded mode, we need to reconnect with fresh auth token
    // since socket.io auth options are set during socket creation.
    if (isEmbedded()) {
      this.socket.disconnect()
      this.socket = this.connect()
    } else {
      this.socket.connect()
    }
  }
}

// Check the socket when the app returns to the foreground
const VisibilityChangeListener = () => {
  if (document.visibilityState === "visible") {
    void SocketManager.check()
  }
}
